import { Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useObserver } from "@shoothill/core";
import { DarwinPage, DarwinPageTitle } from "Globals/Styles/AppStyling";
import { FilterRow } from "Views/Invoices/InvoiceListView";
import { InvoicingListViewModel } from "./InvoicingListViewModel";
import { IEGridItemViewModel } from "Views/Project/Commercial/IEmodels/IEGridItemViewModel";
import { Grid, useRouter } from "@shoothill/core";
import { AppUrls } from "AppUrls";
import { DarwinTablePageContent, DarwinTableSearchBox, DarwinTableToolbar } from "Globals/Styles/AppStyling";
import { StatusCell } from "Globals/Styles/Control/StatusCell.style";
import { TableListSearch } from "Components/Table/TableListSearch";
import { InvoicingListItemViewModel } from "./InvoicingListItemViewModel";
import { MultiSelectView } from "Components/Select/MultiSelect";
import { ColumnHelper, createColumnHelper } from "@tanstack/react-table";
import { formatCurrencyFromPounds, formatDate, formatEmptyValueForTableDash } from "Utils/Format";
import { DarwinTableV2, PaginationDirection, SortOrderDirection } from "Components/Table/DarwinTableV2";
import { InvoicingListFilterViewModel } from "./InvoicingListFilterViewModel";
import { PrimaryButton } from "Components/Buttons/Buttons";

export const InvoicingListView: React.FunctionComponent = () => {
    useEffect(() => {
        IEGridItemViewModel.Instance.setIsCentral(false);
    }, []);

    //const { ieId } = props;
    const [viewModel] = useState(() => InvoicingListViewModel.Instance);
    const { history } = useRouter();
    const router = useRouter();

    const filtersViewModel = InvoicingListFilterViewModel.Instance;

    useEffect(() => {
        viewModel.apiGetAll();

        return () => {
            viewModel.cleanUp();
        };
    }, []);

    const handleRowClick = (rowId: string | number) => {
        const id = rowId as string;
        const url: string = AppUrls.Client.Invoicing.Edit.replace(":invoiceid", id).replace(":approvalMode", "false");
        history.push(url);
    };

    const setSearchString = (value: string) => {
        viewModel.handleSearchChange(value);
    };

    const handleSorting = async (columnName: string, orderBy: SortOrderDirection) => {
        if (viewModel.hasLoaded) {
            filtersViewModel.handleSorting(columnName, orderBy);
            loadData();
        }
    };

    const handlePageChange = async (change: PaginationDirection) => {
        const pageNumber: number = filtersViewModel.model.pageNumber;
        const pageCount: number = filtersViewModel.model.pageCount;

        switch (change) {
            case PaginationDirection.NEXT:
                pageNumber < pageCount ? filtersViewModel.setPageNumber(pageNumber + 1) : filtersViewModel.setPageNumber(1);
                loadData();
                break;
            case PaginationDirection.BACK:
                pageNumber > 1 ? filtersViewModel.setPageNumber(pageNumber - 1) : filtersViewModel.setPageNumber(1);
                loadData();

                break;
            case PaginationDirection.START:
                filtersViewModel.setPageNumber(1);
                loadData();

                break;
            case PaginationDirection.END:
                filtersViewModel.setPageNumber(pageCount);
                loadData();

                break;
            default:
        }
    };

    const handleRowsPerPageChange = async (pageSize: number) => {
        filtersViewModel.handleRowsPerPageChange(pageSize);
        loadData();
    };

    const handlePageValueChange = async (page: number) => {
        filtersViewModel.setPageNumber(page);
        loadData();
    };

    const loadData = async () => {
        await viewModel.apiGetAll();
    };

    const exportCSV = () => {
        viewModel.generateCSV();
    };

    const columnHelper: ColumnHelper<InvoicingListItemViewModel> = createColumnHelper<InvoicingListItemViewModel>();

    const columns = [
        columnHelper.accessor("invoiceNumber", {
            header: () => "Invoice number",
            cell: (info) => <>{formatEmptyValueForTableDash(info.getValue())}</>,
            enableResizing: false,
        }),
        columnHelper.accessor("projectName", {
            header: () => "Project name",
            cell: (info) => <>{formatEmptyValueForTableDash(info.getValue())}</>,
            size: 20,
            enableResizing: false,
        }),
        columnHelper.accessor("invoiceDate", {
            header: () => "Invoice date",
            cell: (info) => <>{formatEmptyValueForTableDash(formatDate(info.getValue() !== null ? info.getValue()!.toString() : ""))}</>,
            size: 100,
            enableResizing: false,
        }),
        columnHelper.accessor("approverRoleName", {
            header: () => "Approver",
            cell: (info) => <>{formatEmptyValueForTableDash(info.getValue())}</>,
            enableResizing: false,
        }),
        columnHelper.accessor("supplierName", {
            header: () => "Supplier",
            cell: (info) => <>{formatEmptyValueForTableDash(info.getValue())}</>,
            enableResizing: false,
        }),
        columnHelper.accessor("invoiceDepartmentName", {
            header: () => "Department",
            cell: (info) => <>{formatEmptyValueForTableDash(info.getValue())}</>,
            enableResizing: false,
        }),
        columnHelper.accessor("statusName", {
            header: () => "Status",
            cell: (info) => (
                <StatusCell
                    className="Unknown"
                    style={{
                        backgroundColor: info.row.original.statusColor,
                        color: info.row.original.statusTextColor,
                        margin: "3px 0",
                        padding: "2px 0",
                    }}
                >
                    {info.row.original.statusNameFormatted}
                </StatusCell>
            ),
            enableResizing: false,
        }),
        columnHelper.accessor("invoiceValue", {
            header: () => "Value",
            cell: (info) => <div style={{ textAlign: "right" }}>{formatCurrencyFromPounds(info.getValue())}</div>,
            enableResizing: false,
        }),
        columnHelper.accessor("approvedDate", {
            header: () => "Approved date",
            cell: (info) => <>{formatEmptyValueForTableDash(formatDate(info.getValue() !== null ? info.getValue()!.toString() : ""))}</>,
            size: 100,
            enableResizing: false,
        }),
        columnHelper.accessor("postedDate", {
            header: () => "Posted Date",
            cell: (info) => <>{formatEmptyValueForTableDash(formatDate(info.getValue() !== null ? info.getValue()!.toString() : ""))}</>,
            size: 100,
            enableResizing: false,
        }),
    ];

    const renderPage = () => {
        return (
            <DarwinPage>
                <DarwinPageTitle>
                    <Typography variant="h1" color="textPrimary">
                        Invoices
                    </Typography>
                </DarwinPageTitle>
                <div style={{ width: "100%", paddingTop: 22 }}>
                    <DarwinTableToolbar>
                        <FilterRow>
                            <MultiSelectView
                                style={{ maxWidth: "200px", marginRight: "20px" }}
                                display="Status"
                                fieldName="statuses"
                                values={filtersViewModel.model.statuses}
                                getOptions={filtersViewModel.getStatusOptions}
                                handleChange={(values: number[] | null) => viewModel.handleStatusChange(values)}
                            />
                            <MultiSelectView
                                style={{ maxWidth: "200px", marginRight: "20px" }}
                                display="Project"
                                fieldName="projects"
                                values={filtersViewModel.model.projects}
                                getOptions={filtersViewModel.getProjectOptions}
                                handleChange={(values: number[] | null) => viewModel.handleProjectChange(values)}
                            />
                            <MultiSelectView
                                style={{ maxWidth: "200px", marginRight: "20px" }}
                                display="Supplier"
                                fieldName="suppliers"
                                values={filtersViewModel.model.suppliers}
                                getOptions={filtersViewModel.getSupplierOptions}
                                handleChange={(values: number[] | null) => viewModel.handleSupplierChange(values)}
                            />
                            <MultiSelectView
                                style={{ maxWidth: "200px", marginRight: "20px" }}
                                display="Department"
                                fieldName="departments"
                                values={filtersViewModel.model.departments}
                                getOptions={filtersViewModel.getDepartmentOptions}
                                handleChange={(values: number[] | null) => viewModel.handleDepartmentChange(values)}
                            />
                        </FilterRow>
                        <br />
                        <Grid columnGap={30} dc={"1fr 1fr"} rowGap={15} tc={"1fr 1fr"}>
                            <DarwinTableSearchBox className="invoice-search-box">
                                <TableListSearch searchString={viewModel.getSearchStringComputed()} setSearchString={setSearchString} />
                            </DarwinTableSearchBox>
                        </Grid>
                        <br />
                        <FilterRow>
                            <PrimaryButton displayName={"Export CSV"} execute={exportCSV} fullWidth={false} canExecute={!viewModel.IsLoading} />
                        </FilterRow>
                    </DarwinTableToolbar>
                    <Grid margin={"20px 0"}>
                        <DarwinTablePageContent className="invoice-Table">
                            <DarwinTableV2
                                columns={columns}
                                data={viewModel.invoicingViewModels}
                                onRowClick={handleRowClick}
                                onSortChange={handleSorting}
                                onChangeRowPerPage={handleRowsPerPageChange}
                                onChangePage={handlePageChange}
                                onChangePageNumber={handlePageValueChange}
                                initialSortColumn={filtersViewModel.model.sortBy}
                                initialSortDirection={SortOrderDirection[filtersViewModel.model.sortDirection as keyof typeof SortOrderDirection]}
                                showPagination={true}
                                totalRowCount={filtersViewModel.model.totalCount}
                                totalPageCount={filtersViewModel.model.pageCount}
                                pageNumber={filtersViewModel.model.pageNumber}
                                pageSize={filtersViewModel.model.pageSize}
                                isProcessing={viewModel.IsLoading}
                            />
                        </DarwinTablePageContent>
                    </Grid>
                </div>
            </DarwinPage>
        );
    };

    return useObserver(() => renderPage());
};
